import { Plane } from "lucide-react";
import { MaskLines, Reveal } from "./Motion";
import { IMG } from "../../data/content";

const Frame = ({ src, alt, label, className = "" }) => (
  <figure className={`group relative overflow-hidden bg-forest ${className}`}>
    <img src={src} alt={alt} className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105" />
    <div className="absolute inset-0 bg-[radial-gradient(70%_60%_at_50%_30%,transparent,rgba(0,125,108,.3))]" />
    <figcaption className="absolute bottom-4 left-4 font-mono text-[12px] uppercase tracking-[0.22em] text-white">{label}</figcaption>
  </figure>
);

const Cell = ({ className = "", children }) => (
  <Reveal className={`flex flex-col justify-between rounded-[1.25rem] p-7 lg:p-8 ${className}`}>{children}</Reveal>
);

export default function Spark() {
  return (
    <section id="origin" className="relative bg-white py-24 text-forest lg:py-36" data-testid="spark-section">
      <div className="wrap">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="eyebrow text-gold-ink">Chapter 02 — The Spark</p>
            <Reveal><p className="mt-6 font-mono text-[28vw] font-medium leading-none tracking-tighter text-outline-forest sm:text-[9rem] lg:text-[11rem]">2012</p></Reveal>
            <p className="mt-2 font-mono text-[12px] uppercase tracking-[0.22em] text-forest-deep/80">Yorkshire, United Kingdom</p>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-forest-deep">
                One evening in a cosy Yorkshire bar, Clive Fernandes and his British colleagues, all passionate lovers of curry, were deep in conversation when an intriguing thought struck them. That single question sparked the beginning of something unique.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <MaskLines
              as="blockquote"
              className="font-display text-[2.1rem] font-medium italic leading-[1.08] sm:text-5xl lg:text-[3.6rem]"
              lineClass="text-forest"
              lines={["“Why is there no", "authentic Indian cider", "to pair with the rich, flavourful", "curries beloved across the UK?”"]}
              data-testid="spark-question"
            />
            <p className="mt-6 font-mono text-[12px] uppercase tracking-[0.22em] text-forest-deep/80">Over a hearty curry in an English pub</p>
            <div className="mt-12 grid grid-cols-2 gap-4">
              <Frame src={IMG("user-1.jpg")} alt="Clive and a friend raising Himachal cider at the Indian Cider Bar" label="Yorkshire · UK" className="frame-clip aspect-[4/5]" />
              <div className="relative">
                <Frame src={IMG("himalaya.jpg")} alt="Himalayan peaks above Himachal" label="Himachal · India" className="arch aspect-[4/5]" />
                <span className="absolute -left-7 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-gold text-forest-deep shadow-xl"><Plane className="h-5 w-5" /></span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20 grid auto-rows-[minmax(210px,auto)] grid-cols-1 gap-4 md:grid-cols-6 lg:mt-28 lg:grid-cols-12" data-testid="mission-bento">
          <Cell className="bg-forest text-white md:col-span-6 lg:col-span-6">
            <p className="eyebrow text-white">The Mission</p>
            <p className="mt-6 font-display text-3xl font-medium leading-tight lg:text-[2.6rem]">Handcraft real cider from 100% authentic Himachali orchard apples and pure mountain water.</p>
          </Cell>
          <Cell className="bg-mist md:col-span-3 lg:col-span-3">
            <p className="font-mono text-6xl font-medium tracking-tighter">100%</p>
            <p className="text-base text-forest-deep">Himachali orchard apples, sourced directly from Himachal Pradesh.</p>
          </Cell>
          <Cell className="bg-gold text-forest-deep md:col-span-3 lg:col-span-3">
            <p className="font-mono text-6xl font-medium tracking-tighter">&lt;5%</p>
            <p className="text-base">ABV. No added sugar. No shortcuts.</p>
          </Cell>
          <Cell className="border border-forest/15 md:col-span-3 lg:col-span-4">
            <p className="eyebrow text-gold-ink">Vision</p>
            <p className="font-display text-2xl font-medium leading-snug">To be the category leader in craft cider, globally.</p>
          </Cell>
          <Cell className="border border-forest/15 md:col-span-3 lg:col-span-4">
            <p className="eyebrow text-gold-ink">Purpose</p>
            <p className="font-display text-2xl font-medium leading-snug">To share the ‘JAI <span className="font-deva">हो!</span>’ joy of our harvest in every bottle.</p>
          </Cell>
          <Reveal className="relative min-h-[240px] overflow-hidden rounded-[1.25rem] md:col-span-6 lg:col-span-4">
            <img src={IMG("user-2.jpg")} alt="Pallets of Himachal Cider ready for export to the UK" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest/80 via-transparent to-transparent" />
            <p className="absolute bottom-5 left-6 right-6 text-sm text-white">Bottled in Himachal. Exported 100% to the UK.</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
